import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import Modal from "react-bootstrap/Modal";
import { HotKeys } from "react-hotkeys";
import Loader from "../../loader/loader";
import "./entity-beneficiary-view.css";
import { BiEdit } from "react-icons/bi";
import { FiChevronLeft, FiChevronRight, FiPlus } from "react-icons/fi";
import { BsBank2 } from "react-icons/bs";
import Button from "react-bootstrap/Button";
import axios from "axios";

const keyMap = {
  PREVIEW: "shift+a",
  EDIT: "shift+e",
};

const Field = ({ label, value }) => (
  <>
    <span className="text-gray-500 py-1.5 text-sm">{label}</span>
    <span className="text-gray-900 font-semibold py-1.5 text-sm">
      {value || "—"}
    </span>
  </>
);

Field.propTypes = {
  label: PropTypes.string,
  value: PropTypes.node,
};

const EntityBeneficiaryView = ({ client, isShown, close, addNew, edit }) => {
  const [beneficiaries, setBeneficiaries] = useState([]);
  const [page, setPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchBeneficiaries = async () => {
      if(!client?.user_id) {
        console.warn("No client user ID found, skipping beneficiary fetch");
        return;
      }
      setIsLoading(true);
      try {
          const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/broker-portal/beneficiaries/by-user/`, {
            headers: {
              Authorization: `JWT ${sessionStorage.getItem("access")}`,
            },
            params: {
              user_id: client.user_id,
            },
           });
        setBeneficiaries(response.data);
      } catch (ex) {
        console.log(ex.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchBeneficiaries();
  }, [client]);

  const pageDown = () => {
    if (page !== 0) {
      setPage(page - 1);
    }
  };

  const pageUp = () => {
    if (page + 1 !== beneficiaries.length) {
      setPage(page + 1);
    }
  };

  const handlers = {
    PREVIEW: () => addNew(),
    EDIT: () => edit(beneficiaries[page]),
  };

  const currentBen = beneficiaries[page];

  return (
    <HotKeys className="w-100" keyMap={keyMap} handlers={handlers}>
      <Modal show={isShown} animation={false} onHide={close}>
        <Modal.Header>
          <div className="flex items-center justify-between w-full">
            <div>
              <span className="text-xs text-gray-400 uppercase tracking-wide font-medium">
                Beneficiaries for
              </span>
              <h3 className="text-lg font-bold text-gray-900 m-0 mt-1">
                {client.firstname} {client.surname}
              </h3>
            </div>
          </div>
        </Modal.Header>
        {isLoading ? (
          <Modal.Body>
            <div className="flex justify-center py-8">
              <Loader />
            </div>
          </Modal.Body>
        ) : (
          <>
            <Modal.Body className="!bg-gray-50 !p-4">
              {beneficiaries.length > 0 ? (
                <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
                  <div className="border-b border-gray-200 px-4 py-3">
                    <h3 className="text-base font-bold text-gray-900 m-0 flex items-center gap-2">
                      <BsBank2 size={18} className="text-blue-500" />
                      {currentBen?.name}
                      {currentBen?.crypto && (
                        <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">
                          Crypto
                        </span>
                      )}
                    </h3>
                  </div>
                  <div className="px-4 py-3">
                    <div className="grid grid-cols-2 gap-x-6 gap-y-2">
                      <Field
                        label="Friendly Name"
                        value={currentBen?.friendly_name}
                      />
                      <Field
                        label="Account Number"
                        value={currentBen?.beneficiary_account_number}
                      />
                      <Field
                        label="Currency"
                        value={currentBen?.currency}
                      />
                      <Field
                        label="Bank Name"
                        value={currentBen?.bank_name}
                      />
                      <Field
                        label="SWIFT / BIC"
                        value={currentBen?.swift_or_bic}
                      />
                      <Field
                        label="Branch Code"
                        value={currentBen?.bank_branch_code}
                      />
                      <Field
                        label="Bank Address"
                        value={currentBen?.bank_address}
                      />
                      <Field
                        label="Bank Country"
                        value={currentBen?.bank_country}
                      />
                      <Field
                        label="Beneficiary Email"
                        value={currentBen?.beneficiary_email}
                      />
                      <Field
                        label="Beneficiary Cell"
                        value={currentBen?.beneficiary_cell}
                      />
                      <Field
                        label="Beneficiary Address"
                        value={currentBen?.beneficiary_address}
                      />
                      <Field
                        label="Client Reference"
                        value={currentBen?.client_reference_beneficiary}
                      />
                      <Field
                        label="Recipient Reference"
                        value={currentBen?.recipient_reference_beneficiary}
                      />
                      <Field
                        label="Notify SMS"
                        value={currentBen?.notify_sms ? "Yes" : "No"}
                      />
                      <Field
                        label="Notify Email"
                        value={currentBen?.notify_email ? "Yes" : "No"}
                      />
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-8 gap-3">
                  <span className="text-gray-400 text-sm">No beneficiaries found</span>
                </div>
              )}
            </Modal.Body>

            <Modal.Footer>
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-3">
                  {beneficiaries.length > 0 && (
                    <>
                      <button
                        onClick={pageDown}
                        disabled={page === 0}
                        className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <FiChevronLeft size={18} />
                      </button>
                      <span className="text-sm font-medium text-gray-700">
                        {page + 1} of {beneficiaries.length}
                      </span>
                      <button
                        onClick={pageUp}
                        disabled={page + 1 === beneficiaries.length}
                        className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                      >
                        <FiChevronRight size={18} />
                      </button>
                    </>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline-success"
                    size="sm"
                    className="flex items-center gap-1"
                    onClick={addNew}
                  >
                    <FiPlus size={16} />
                    Add
                  </Button>
                  {beneficiaries.length > 0 && (
                    <Button
                      variant="outline-primary"
                      size="sm"
                      className="flex items-center gap-1"
                      onClick={() => edit(currentBen)}
                    >
                      <BiEdit size={16} />
                      Edit
                    </Button>
                  )}
                </div>
              </div>
            </Modal.Footer>
          
          </>
        )}
      </Modal>
    </HotKeys>
  );
};

EntityBeneficiaryView.propTypes = {
  client: PropTypes.object,
  isShown: PropTypes.bool,
  close: PropTypes.func,
  addNew: PropTypes.func,
  edit: PropTypes.func,
};

export default EntityBeneficiaryView;
